import posthog from 'posthog-js';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { ActivationResume } from '@/Domain/utils/safeNextPath';

// Booleans only — the raw `next` carries the OAuth state and code_challenge. Emitted at boot with
// replayed=false and again from the navigation, so accepted-without-replayed isolates a drop after
// validation. Never allowed to throw: it runs ahead of the conversion below and of the resume
// navigation itself, neither of which may be lost to a telemetry failure.
export function reportActivationResume(resume: ActivationResume, replayed: boolean): void {
  try {
    posthog.capture(
      PostHogEventEnum.ActivationNextReplayed,
      {
        next_present: resume.present,
        next_accepted: resume.resume !== null,
        next_replayed: replayed,
      },
      // Survives the resume navigation.
      { transport: 'sendBeacon' },
    );
  } catch (error) {
    console.error('Failed to capture activation resume telemetry', error);
  }
}

// Fire the GTM `account_created` conversion on email activation, then navigate: to the
// same-origin `next` carried through signup (e.g. the MCP-authorize URL), or to `/` so the
// server's authenticated redirect chain forwards into the app — the root shell itself is a
// login form, so ending here without navigating would strand the user on it. Kept out of
// analytics.ts on purpose: importing that module at boot would eagerly load the GTM container.
//
// The event payload is FLAT (`{ event, ...regProps }`, not nested under `eventProperties`) —
// the existing GTM conversion tag reads it that way, so this can't delegate to trackAndNavigate.
export function fireAccountCreatedConversion(
  regProps: Record<string, unknown>,
  resumePath: string | null,
  navTimeoutMs: number,
  onResume?: () => void,
): void {
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  const target = resumePath ?? '/';
  // Resume only AFTER the conversion tags fire (eventCallback); the timeout is the fallback for a
  // blocked/absent GTM container, and `navigated` makes the two paths fire the navigation once.
  let navigated = false;
  const resume = (): void => {
    if (navigated) return;
    navigated = true;
    try {
      // Before assign(), so the beacon is queued while the document is still alive.
      onResume?.();
    } finally {
      // finally, or a throwing callback strands the user on the page they were resuming from —
      // `navigated` has already disarmed the timeout fallback by this point.
      window.location.assign(target);
    }
  };
  dataLayer.push({
    event: 'account_created',
    ...regProps,
    eventCallback: resume,
    eventTimeout: navTimeoutMs,
  });
  window.setTimeout(resume, navTimeoutMs);
}
